// Family sources — the cloud catalog plus any local folders the user registered, like a set of
// NuGet package sources. Local sources are Revit-only: the browser has no filesystem, so there it is
// always just the cloud catalog and the switcher is hidden.

import { transport } from "@/transport";
import { PICKER_TIMEOUT_MS } from "@/transport/types";
import type { FamilySource } from "./types";

const CLOUD: FamilySource = {
  id: "cloud",
  // A fallback only: the cloud source is always labelled from `sources.cloud`, so that this one
  // name is not the one string in the app that stays English.
  name: "Online",
  kind: "cloud",
  path: null,
  enabled: true,
  removable: false,
};

interface SourcesState {
  sources: FamilySource[];
  selectedId: string;
  loading: boolean;
}

const state = $state<SourcesState>({
  sources: [CLOUD],
  selectedId: "cloud",
  loading: false,
});

async function load(): Promise<void> {
  // Local sources exist only inside Revit; in the browser there is nothing to ask.
  if (!transport.caps.revit) {
    state.sources = [CLOUD];
    return;
  }

  state.loading = true;
  try {
    const res = await transport.invoke<{ sources: FamilySource[] }>("ListSources");
    state.sources = res.sources?.length ? res.sources : [CLOUD];
  } catch {
    state.sources = [CLOUD];
  } finally {
    state.loading = false;
  }

  // Keep the selection valid after a reload (e.g. the selected source was removed).
  if (!state.sources.some((s) => s.id === state.selectedId)) state.selectedId = "cloud";
}

function select(id: string): void {
  state.selectedId = id;
}

/** Picks a folder and registers it as a source. Returns an error message, or null on success/cancel. */
async function addFolder(): Promise<string | null> {
  try {
    const picked = await transport.invoke<{ path: string }>("PickFolder", null, { timeoutMs: PICKER_TIMEOUT_MS });
    if (!picked.path) return null; // cancelled

    const res = await transport.invoke<{ ok: boolean; error: string | null; source: FamilySource | null }>(
      "AddLocalSource",
      { path: picked.path },
    );
    if (!res.ok) return res.error ?? "The folder could not be added.";

    await load();
    if (res.source) state.selectedId = res.source.id;
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
}

async function remove(id: string): Promise<void> {
  try {
    await transport.invoke("RemoveSource", { id });
  } catch {
    /* removing is best-effort; a failure just leaves the source listed */
  }
  if (state.selectedId === id) state.selectedId = "cloud";
  await load();
}

function selected(): FamilySource {
  return state.sources.find((s) => s.id === state.selectedId) ?? CLOUD;
}

export const sources = {
  get state() {
    return state;
  },
  load,
  select,
  addFolder,
  remove,
  selected,
};
